/**
 * Structural blocks shared across the förhandling family.
 *
 * These carry no legal wording of their own — they place the letterhead, the
 * party panels, the preamble and the signature block. That is why they sit on
 * the `shared` plane: reviewed once for everyone, not per document.
 */
import { details, formatAddress, h, img, nodes, p, section, table, val, } from '@hyranai/regta-document-ir';
import { arr, str } from '../lib.js';
import { signingBlock as sharedSigningBlock } from './signing.js';
const SHARED = { plane: 'shared', status: 'published', language: 'sv-SE' };
/** Brand-mark size bound (matches the legacy OwnerLogo/TenantLogo). */
const LOGO_MAX = { maxHeight: '1.2cm', maxWidth: '5cm' };
export const letterhead = {
    ...SHARED,
    blockKey: 'common.letterhead',
    version: 'v1',
    title: 'Sidhuvud',
    summary: 'Repeating page header: owner + customer brand marks, document title and form number.',
    slots: [
        { key: 'title', label: 'Dokumenttitel', type: 'text', required: true },
        { key: 'form_number', label: 'Formulärnummer', type: 'text' },
    ],
    compile: (input, ctx) => {
        const title = str(input.title);
        const formNumber = str(input.form_number);
        const branding = ctx.custom?.branding;
        const hasBrand = !!(branding &&
            (branding.ownerLogoSrc || branding.ownerName || branding.customerLogoSrc));
        // No branding supplied: the plain title/form header, unchanged from
        // before, so documents without branding compile identically.
        if (!hasBrand) {
            return nodes(title ? p(title, { variant: 'caption' }) : null, formNumber ? p(formNumber, { variant: 'caption', align: 'right' }) : null);
        }
        // Owner mark on the left (logo, else name as a wordmark); customer mark on
        // the right (logo only — a missing one stays invisible). Title + form
        // number stack under the owner mark, as in the legacy TemplateLetterhead.
        const ownerMark = branding.ownerLogoSrc
            ? p(img(branding.ownerLogoSrc, { ...LOGO_MAX, alt: branding.ownerName ?? '' }))
            : branding.ownerName
                ? p(branding.ownerName, { variant: 'caption' })
                : null;
        const customerMark = branding.customerLogoSrc
            ? p(img(branding.customerLogoSrc, { ...LOGO_MAX, alt: branding.customerName ?? '' }), { align: 'right' })
            : null;
        const left = nodes(ownerMark, title ? p(title, { variant: 'lead' }) : null, formNumber ? p(formNumber, { variant: 'caption' }) : null);
        const right = nodes(customerMark);
        return [
            table([
                { key: 'brand-left', align: 'left', width: 75 },
                { key: 'brand-right', align: 'right', width: 25 },
            ], [[left, right]], { variant: 'plain' }),
        ];
    },
};
/** The mark the legacy TemplateFooter prints on the right of every page. */
export const FOOTER_MARK = 'Regta';
/**
 * The repeating page footer — the legacy `TemplateFooter`: form number on
 * the left, the "Regta" mark on the right. An instance may pass `mark`
 * (`''` hides it).
 */
export const pageFooter = {
    ...SHARED,
    blockKey: 'common.page-footer',
    version: 'v1',
    title: 'Sidfot',
    summary: 'Repeating page footer: the form number on the left, the Regta mark on the right.',
    slots: [{ key: 'form_number', label: 'Formulärnummer', type: 'text' }],
    compile: (input) => {
        const formNumber = str(input.form_number);
        const mark = typeof input.mark === 'string' ? str(input.mark) : FOOTER_MARK;
        if (!formNumber && !mark)
            return null;
        return [
            table([
                { key: 'form-number', align: 'left', width: 50 },
                { key: 'mark', align: 'right', width: 50 },
            ], [[formNumber ? [p(formNumber)] : [], mark ? [p(mark, { align: 'right' })] : []]], { variant: 'plain' }),
        ];
    },
};
/**
 * The document's opening line — "Undertecknande parter har denna dag …".
 * Italic, as in the legacy templates; an instance passes `variant: 'intro'`
 * for the compact 10px form that sits under an Avtalsnummer line
 * (bostadshyra, lokalhyra, blockhyra, studentbostad, tillägg hyresgästanpassning).
 */
export const preamble = {
    ...SHARED,
    blockKey: 'common.preamble',
    version: 'v1',
    title: 'Ingress',
    summary: 'The document’s opening sentence, above the first numbered point.',
    slots: [{ key: 'text', label: 'Ingress', type: 'textarea', required: true }],
    compile: (input) => str(input.text)
        ? [p(str(input.text), { variant: input.variant === 'intro' ? 'intro' : 'preamble' })]
        : null,
};
/**
 * A centred document title with its statutory subtitle — the letter-style
 * opening used by the meddelanden.
 */
export const documentHeading = {
    ...SHARED,
    blockKey: 'common.document-heading',
    version: 'v1',
    title: 'Dokumentrubrik',
    summary: 'Centred document title with an italic statutory subtitle.',
    slots: [
        { key: 'title', label: 'Rubrik', type: 'text', required: true },
        { key: 'subtitle', label: 'Underrubrik', type: 'text' },
    ],
    compile: (input) => nodes(h(1, str(input.title) ?? '', { align: 'center' }), str(input.subtitle) ? p(str(input.subtitle), { align: 'center', variant: 'caption' }) : null),
};
/**
 * One party's details as a numbered point.
 *
 * @deprecated Archived: it formats `ctx.parties` itself, which breaks the
 * layering rule (blocks print slot values; parties arrive through autofill
 * bindings). Use `partyDetailsBlock` from `./fields.ts`. No composition
 * references it.
 */
export const partyPanel = {
    ...SHARED,
    status: 'archived',
    blockKey: 'common.party-panel',
    version: 'v1',
    title: 'Part',
    summary: 'Deprecated — use partyDetailsBlock (slot values with party autofill). Formats ctx.parties directly.',
    slots: [
        { key: 'ref', label: 'Referens', type: 'text', required: true },
        { key: 'number', label: 'Punktnummer', type: 'text' },
        { key: 'title', label: 'Rubrik', type: 'text', required: true },
        { key: 'role', label: 'Partsroll', type: 'party', required: true },
        { key: 'ordinal', label: 'Ordning', type: 'number' },
    ],
    compile: (input, ctx) => {
        const ref = str(input.ref);
        const title = str(input.title);
        const role = str(input.role);
        if (!ref || !title || !role)
            return null;
        const party = ctx.parties.slot(role, Number(input.ordinal ?? 0));
        const idLabel = party?.kind === 'person' ? 'Personnummer' : 'Organisationsnummer';
        const number = str(input.number);
        return [
            section(ref, title, [
                details([
                    ['Namn', [p([val(party?.name ?? null)])]],
                    [idLabel, [p([val(party?.legalId ?? null)])]],
                    ['Adress', [p([val(formatAddress(party?.address))])]],
                    ['E-post', [p([val(party?.email ?? null)])]],
                    ['Telefon', [p([val(party?.phone ?? null)])]],
                ], { variant: 'boxed' }),
            ], number ? { numbering: 'fixed', number, keepTogether: true } : { keepTogether: true }),
        ];
    },
};
/**
 * `common.signing@v1` — the legacy SigningBlock. Implemented in
 * `./signing.ts` (statement/place-date coupling, gated panels, frame).
 */
export const signingBlock = sharedSigningBlock;
/** A free-text point that hides itself when empty — "Eventuella kopplade avtal". */
export const optionalTextPoint = {
    ...SHARED,
    blockKey: 'common.optional-point',
    version: 'v1',
    title: 'Valfri punkt',
    summary: 'A numbered point that compiles to nothing when its text is empty.',
    slots: [
        { key: 'ref', label: 'Referens', type: 'text', required: true },
        { key: 'number', label: 'Punktnummer', type: 'text' },
        { key: 'title', label: 'Rubrik', type: 'text', required: true },
        { key: 'value_key', label: 'Fältnyckel', type: 'text', required: true },
    ],
    compile: (input, ctx) => {
        const ref = str(input.ref);
        const title = str(input.title);
        const key = str(input.value_key);
        if (!ref || !title || !key)
            return null;
        const text = str(input[key]);
        if (!text && ctx.mode !== 'blank')
            return null;
        const number = str(input.number);
        return [
            section(ref, title, [p([val(text, { sourceKey: key })])], {
                keepTogether: true,
                ...(number ? { numbering: 'fixed', number } : {}),
            }),
        ];
    },
};
export const SHARED_BLOCKS = [
    letterhead,
    pageFooter,
    preamble,
    documentHeading,
    partyPanel,
    signingBlock,
    optionalTextPoint,
];
export { arr };
//# sourceMappingURL=shared.js.map